/* eslint-disable */
// @ts-nocheck

import { forwardRef, useEffect, useRef, useState } from 'react'

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'

interface Option {
  id: number
  name: string
}

interface AsyncSelectProps {
  _value: number
  onChange: (_value: number) => void
  onSearch: (_query: string) => Promise<Option[]>
  placeholder: string
  label: string
  error?: string
  required?: boolean
  disabled?: boolean
  searchQuery?: string
  setSearchQuery?: (_q: string) => void
  debouncedSearchQuery?: string
}

// Componente de esqueleto para el estado de carga inicial
const InitialLoadingSkeleton = () => (
  <div className="space-y-2 p-2">
    {Array.from({ length: 5 }).map((_, index) => (
      <div className="flex animate-pulse items-center space-x-3" key={index}>
        <div className="h-4 w-2/3 rounded bg-gray-200"></div>
        <div className="h-3 w-1/3 rounded bg-gray-100"></div>
      </div>
    ))}
  </div>
)

// Componente de esqueleto para búsqueda
const SearchLoadingSkeleton = () => (
  <div className="space-y-2 p-2">
    {Array.from({ length: 3 }).map((_, index) => (
      <div className="flex animate-pulse items-center space-x-3" key={index}>
        <div className="h-4 w-1/2 rounded bg-gray-200"></div>
        <div className="h-3 w-1/4 rounded bg-gray-100"></div>
      </div>
    ))}
  </div>
)

// Cache simple para almacenar resultados de búsqueda
const searchCache = new Map<string, { data: Option[]; timestamp: number }>()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

// NUEVO: función para filtrar localmente
const localFilter = (options: Option[], query: string) => {
  if (!query) return options
  return options.filter((opt) =>
    opt.name.toLowerCase().includes(query.toLowerCase())
  )
}

// Generar un ID único para cada instancia del componente
let instanceCounter = 0
const getInstanceId = () => {
  instanceCounter++
  return `async-select-${instanceCounter}`
}

const AsyncSelect = forwardRef<HTMLDivElement, AsyncSelectProps>(
  (
    {
      _value: value,
      onChange,
      onSearch,
      placeholder,
      label,
      error: _error,
      disabled = false,
      searchQuery: controlledSearchQuery,
      setSearchQuery: setControlledSearchQuery,
      debouncedSearchQuery: _debouncedSearchQuery,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [internalSearchQuery, setInternalSearchQuery] = useState('')
    const searchQuery =
      controlledSearchQuery !== undefined
        ? controlledSearchQuery
        : internalSearchQuery
    const setSearchQuery =
      setControlledSearchQuery !== undefined
        ? setControlledSearchQuery
        : setInternalSearchQuery
    const [options, setOptions] = useState<Option[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedOption, setSelectedOption] = useState<Option | null>(null)
    const [initialLoading, setInitialLoading] = useState(false)

    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const instanceId = useRef(getInstanceId())

    // Función para obtener datos del cache o hacer la búsqueda
    const getCachedOrSearch = async (query: string): Promise<Option[]> => {
      const cacheKey = `${instanceId.current}-${query.toLowerCase()}`
      const cached = searchCache.get(cacheKey)

      // Verificar si hay datos en cache y no han expirado
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        // Simular un pequeño delay para mostrar que viene del cache
        await new Promise((resolve) => setTimeout(resolve, 100))
        return cached.data
      }

      // NUEVO: Si hay cache de búsqueda vacía, filtrar localmente
      const allCacheKey = `${instanceId.current}-`
      const allCached = searchCache.get(allCacheKey)
      if (
        allCached &&
        Date.now() - allCached.timestamp < CACHE_DURATION &&
        query
      ) {
        const filtered = localFilter(allCached.data, query)
        // No guardamos este resultado filtrado en cache para queries específicas
        return filtered
      }

      // Si no hay cache válido, hacer la búsqueda
      const results = await onSearch(query)

      // Guardar en cache
      searchCache.set(cacheKey, {
        data: results,
        timestamp: Date.now(),
      })

      return results
    }

    // Lógica de búsqueda: si se pasa debouncedSearchQuery, usarla, si no, usar searchQuery local con debounce interno
    useEffect(() => {
      const queryToUse =
        _debouncedSearchQuery !== undefined
          ? _debouncedSearchQuery
          : searchQuery
      if (queryToUse === '' && options.length > 0) {
        return
      }
      setLoading(true)
      getCachedOrSearch(queryToUse)
        .then(setOptions)
        .catch(() => setOptions([]))
        .finally(() => setLoading(false))
    }, [_debouncedSearchQuery, searchQuery, onSearch])

    // Cargar datos iniciales cuando se abre
    useEffect(() => {
      if (isOpen && searchQuery === '' && options.length === 0) {
        setInitialLoading(true)
        handleSearch('').finally(() => setInitialLoading(false))
      }
    }, [isOpen])

    // Encontrar la opción seleccionada cuando cambia el valor
    useEffect(() => {
      if (value && options.length > 0) {
        const selected = options.find((option) => option.id === value)
        setSelectedOption(selected || null)
      } else {
        setSelectedOption(null)
      }
    }, [value, options])

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
          setSearchQuery('')
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSearch = async (query: string) => {
      // Si es búsqueda vacía y ya tenemos datos, no hacer request
      if (query === '' && options.length > 0) {
        return
      }

      setLoading(true)
      try {
        const results = await getCachedOrSearch(query)
        setOptions(results)
      } catch {
        setOptions([])
      } finally {
        setLoading(false)
      }
    }

    const handleOptionSelect = (option: Option) => {
      onChange(option.id)
      setSelectedOption(option)
      setIsOpen(false)
      setSearchQuery('')
    }

    const handleInputFocus = () => {
      setIsOpen(true)
      if (searchInputRef.current) {
        searchInputRef.current.focus()
      }
    }

    return (
      <div
        aria-invalid={!!_error}
        className="relative"
        ref={ref || dropdownRef}
      >
        <label
          className={`mb-1 block text-sm font-medium ${_error ? 'text-destructive' : 'text-gray-700'}`}
        >
          {label}
        </label>

        {/* Trigger button */}
        <button
          aria-invalid={!!_error}
          className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${
            _error ? 'border-destructive' : 'border-input'
          } ${
            disabled
              ? 'cursor-not-allowed bg-gray-100'
              : 'hover:border-gray-400'
          }`}
          disabled={disabled}
          onClick={handleInputFocus}
          type="button"
        >
          <div className="flex items-center justify-between">
            <span
              className={selectedOption ? 'text-gray-900' : 'text-gray-500'}
            >
              {selectedOption ? selectedOption.name : placeholder}
            </span>
          </div>
        </button>

        {_error && <p className="mt-1 text-sm text-destructive">{_error}</p>}

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg">
            {/* Search input */}
            <div className="border-b border-gray-200 p-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  autoFocus
                  className="flex h-9 w-full rounded-md border border-input bg-transparent py-1 pl-8 pr-3 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar..."
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                />
              </div>
            </div>

            {/* Options list */}
            <div className="max-h-48 overflow-y-auto">
              {initialLoading ? (
                // Esqueletos para carga inicial
                <InitialLoadingSkeleton />
              ) : loading ? (
                // Esqueletos para búsqueda
                <SearchLoadingSkeleton />
              ) : options.length > 0 ? (
                <>
                  {options.map((option) => (
                    <button
                      className={`w-full px-3 py-2 text-left text-sm focus:bg-blue-50 focus:outline-none hover:bg-blue-50 ${
                        option.id === value
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-900'
                      }`}
                      key={option.id}
                      onClick={() => handleOptionSelect(option)}
                      type="button"
                    >
                      {option.name}
                    </button>
                  ))}
                </>
              ) : (
                <div className="p-3 text-center text-sm text-gray-500">
                  {searchQuery
                    ? 'No se encontraron resultados'
                    : 'No hay opciones disponibles'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
)
AsyncSelect.displayName = 'AsyncSelect'
export default AsyncSelect
